import {
  MatrixQuestion,
  MatrixQuestionCreateApiObject,
  MatrixQuestionResult,
  MatrixQuestionResults,
  MatrixQuestionSearchParams,
  MatrixQuestionSearchResult,
  MatrixQuestionUpdateApiObject,
  QuestionFilterIds,
} from "types";
import { max, min } from "date-fns";

import API from "api/client";
import { envConfig } from "config";
import { parseDateLike } from "utils";
import questionsApi from "api/services/QuestionsAPI";

const { matrixQuestionsAPI } = envConfig;

/**
 * Creates a new matrix question.
 */
const createMatrixQuestion = (req: MatrixQuestionCreateApiObject): Promise<MatrixQuestion> => {
  return API.post(`${matrixQuestionsAPI}`, req).then((res) => res.data);
};

/**
 * Gets a matrix question by ID.
 */
const getMatrixQuestion = (matrixAskableId: number | string): Promise<MatrixQuestion> => {
  return API.get(`${matrixQuestionsAPI}${matrixAskableId}`).then((res) => res.data);
};

/**
 * Searches for matrix questions based on the provided parameters.
 */
const getMatrixQuestions = ({
  query,
  orderByTerm,
  orderTerm,
}: MatrixQuestionSearchParams): Promise<MatrixQuestionSearchResult[]> => {
  return API.get(`${matrixQuestionsAPI}`, {
    params: { query, orderBy: orderByTerm, order: orderTerm },
  }).then((res) => res.data);
};

/**
 * Updates an existing matrix question.
 */
const updateMatrixQuestion = (
  matrixAskableId: string | number,
  req: MatrixQuestionUpdateApiObject,
): Promise<MatrixQuestion> => {
  return API.put(`${matrixQuestionsAPI}${matrixAskableId}`, req).then((res) => res.data);
};

/**
 * Fetches the results for each row of a matrix question.
 * This is intentionally done serially to avoid overwhelming the API with requests.
 * If the matrix question has no rows, it returns an empty array.
 *
 * IMPORTANT: This function follows a fail-fast approach - if any single row
 * fails to fetch, the entire operation will reject and no results will be returned.
 * This ensures data consistency but means partial results are not supported.
 *
 * Future consideration: We could implement partial failure handling to skip
 * failed rows and return available results with error indicators.
 */
const fetchMatrixQuestionResults = async (
  matrixQuestion: MatrixQuestion,
  filterIds: QuestionFilterIds,
): Promise<MatrixQuestionResults> => {
  if (!matrixQuestion || !matrixQuestion.matrixAskableRows || matrixQuestion.matrixAskableRows.length === 0) {
    return {
      data: [],
      startTime: null,
      endTime: null,
      totalCount: 0,
    };
  }

  const results: MatrixQuestionResult[] = [];
  for (const row of matrixQuestion.matrixAskableRows) {
    const result = await questionsApi.getResults({
      questionId: row.questionRefid || 0,
      ...filterIds,
    });
    results.push({
      answerChoices: result.results.map((choice) => ({
        count: choice.count,
        label: choice.label,
        pct: Math.round(choice.pct * 100),
      })),
      rowLabel: row.label,
      totalCount: result.totalCount || 0,
      startTime: result.startTime || null,
      endTime: result.endTime || null,
      questionId: result.questionId,
    });
  }

  const startDates = results.map((x) => parseDateLike(x.startTime)).filter((x) => !!x);
  const endDates = results.map((x) => parseDateLike(x.endTime)).filter((x) => !!x);

  return {
    data: results,
    startTime: startDates.length > 0 ? min(startDates) : null,
    endTime: endDates.length > 0 ? max(endDates) : null,
    totalCount: Math.max(...[0, ...results.map((x) => x.totalCount || 0)]),
  };
};

/**
 * Favorite a matrix group by ID.
 */
const favoriteMatrixGroup = (matrixAskableId: number | string): Promise<MatrixQuestion> => {
  return API.post(`${matrixQuestionsAPI}${matrixAskableId}/favorite`).then((res) => res.data);
};

/**
 * Unfavorite a matrix question by ID.
 */
const unfavoriteMatrixQuestion = (matrixAskableId: number | string): Promise<void> => {
  return API.delete(`${matrixQuestionsAPI}${matrixAskableId}/favorite`).then((res) => res.data);
};

export default {
  create: createMatrixQuestion,
  get: getMatrixQuestion,
  list: getMatrixQuestions,
  update: updateMatrixQuestion,
  favorite: favoriteMatrixGroup,
  unfavorite: unfavoriteMatrixQuestion,
  fetchResults: fetchMatrixQuestionResults,
} as const;
